import type { CellColor, CellLayer, Fold, Grid, Puzzle, PuzzleDifficulty } from "../types.ts";
import { createGrid } from "./grid.ts";
import { applyFold } from "./fold.ts";
import { getVisibleSurface } from "./stack.ts";
import { mulberry32, randInt, randPick, shuffle } from "./prng.ts";

const ALL_COLORS: CellColor[] = [
  "#ef4444", // red
  "#3b82f6", // blue
  "#eab308", // yellow
  "#22c55e", // green
  "#a855f7", // purple
  "#f97316", // orange
];

export const DIFFICULTY_TIERS: Record<string, PuzzleDifficulty> = {
  easy:   { gridSize: [4, 4], numFolds: 2, numColors: 3 },
  medium: { gridSize: [5, 5], numFolds: 3, numColors: 4 },
  hard:   { gridSize: [6, 6], numFolds: 4, numColors: 5 },
  expert: { gridSize: [7, 7], numFolds: 5, numColors: 6 },
};

export function generatePuzzle(seed: number, difficulty: PuzzleDifficulty): Puzzle {
  const rng = mulberry32(seed);
  const [targetRows, targetCols] = difficulty.gridSize;
  const { numFolds, numColors } = difficulty;

  // Pick a color palette
  const palette = shuffle(rng, ALL_COLORS).slice(0, numColors);

  // We work backwards: start with a target, then unfold.
  // But it's simpler to: create a full grid, define folds, apply them, and record the result.
  // Strategy: create a large grid, pick folds that reduce it, apply them, record target.

  // Determine the starting grid size by simulating unfolds.
  // Each fold halves one dimension (approximately). So we start bigger.
  // For simplicity: generate a starting grid, pick valid folds, apply them.

  let bestPuzzle: Puzzle | null = null;

  for (let attempt = 0; attempt < 50; attempt++) {
    const result = tryGeneratePuzzle(rng, targetRows, targetCols, numFolds, palette);
    if (result) {
      bestPuzzle = {
        id: `puzzle-${seed}`,
        seed,
        initialGrid: result.initialGrid,
        target: result.target,
        solution: result.folds,
        difficulty,
      };
      break;
    }
  }

  // Fallback: create a trivial puzzle
  if (!bestPuzzle) {
    const grid = createColoredGrid(targetRows, targetCols, palette, rng);
    const fold: Fold = { axis: "vertical", line: Math.ceil(targetCols / 2), direction: "left" };
    const folded = applyFold(grid, fold);
    bestPuzzle = {
      id: `puzzle-${seed}`,
      seed,
      initialGrid: grid,
      target: getVisibleSurface(folded),
      solution: [fold],
      difficulty,
    };
  }

  return bestPuzzle;
}

interface GeneratedPuzzle {
  initialGrid: Grid;
  target: CellColor[][];
  folds: Fold[];
}

function tryGeneratePuzzle(
  rng: () => number,
  baseRows: number,
  baseCols: number,
  numFolds: number,
  palette: CellColor[]
): GeneratedPuzzle | null {
  // Start with the base size and expand by picking folds in reverse
  // Each "unfold" doubles one dimension (or extends it)
  let rows = baseRows;
  let cols = baseCols;
  const reverseFolds: Fold[] = [];

  // Pick random folds that will be applied to the starting grid
  // We need to pick folds for a grid that starts at some expanded size
  // and ends at baseRows x baseCols after all folds.

  // Forward approach: start with an expanded grid, pick folds to shrink it
  // Calculate expanded dimensions
  let expandedRows = rows;
  let expandedCols = cols;

  const foldPlan: Array<{ axis: "horizontal" | "vertical" }> = [];
  for (let i = 0; i < numFolds; i++) {
    // Alternate or random axis
    if (rng() < 0.5) {
      foldPlan.push({ axis: "horizontal" });
      expandedRows = expandedRows + randInt(rng, 1, expandedRows);
    } else {
      foldPlan.push({ axis: "vertical" });
      expandedCols = expandedCols + randInt(rng, 1, expandedCols);
    }
  }

  // Cap the grid size to something reasonable
  if (expandedRows > 12 || expandedCols > 12) return null;

  // Create the initial colored grid
  const grid = createColoredGrid(expandedRows, expandedCols, palette, rng);

  // Now pick actual fold lines and directions
  let currentGrid = grid;
  const folds: Fold[] = [];

  for (const plan of foldPlan) {
    if (plan.axis === "horizontal" && currentGrid.rows > 1) {
      const line = randInt(rng, 1, currentGrid.rows - 1);
      const direction: "up" | "down" = rng() < 0.5 ? "up" : "down";
      const fold: Fold = { axis: "horizontal", line, direction };
      currentGrid = applyFold(currentGrid, fold);
      folds.push(fold);
    } else if (plan.axis === "vertical" && currentGrid.cols > 1) {
      const line = randInt(rng, 1, currentGrid.cols - 1);
      const direction: "left" | "right" = rng() < 0.5 ? "left" : "right";
      const fold: Fold = { axis: "vertical", line, direction };
      currentGrid = applyFold(currentGrid, fold);
      folds.push(fold);
    }
  }

  if (folds.length !== numFolds) return null;

  // The target is the visible surface after all folds
  const target = getVisibleSurface(currentGrid);

  // Verify the target is interesting (has multiple colors, not all null)
  const colorSet = new Set<CellColor>();
  for (const row of target) {
    for (const cell of row!) {
      if (cell !== null) colorSet.add(cell);
    }
  }
  if (colorSet.size < 2) return null;

  return { initialGrid: grid, target, folds };
}

function createColoredGrid(
  rows: number,
  cols: number,
  palette: CellColor[],
  rng: () => number
): Grid {
  const colors: CellColor[][] = [];
  for (let r = 0; r < rows; r++) {
    colors[r] = [];
    for (let c = 0; c < cols; c++) {
      // ~40% chance of being colored, 60% transparent
      if (rng() < 0.4) {
        colors[r]![c] = randPick(rng, palette);
      } else {
        colors[r]![c] = null;
      }
    }
  }
  return createGrid(rows, cols, colors);
}
